import React, { memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppModal,
  Col,
  Row,
  UiButton,
  UiDivider,
  UiFlex,
  UiIcon,
  UiImage,
  UiSimpleGrid,
  UiStackDivider,
  UiText,
  UiVStack,
  useBreakpointValue,
} from "~/components/elements";

import blueCheckMark from "~/assets/img/v2/blue_check_mark.png";
import { AppDispatch } from "~/redux/root-store";
import { ModalProps } from "~/types/general";

import upgradeChristmas from "~/assets/img/v2/available-styles/upgrade_chrismas.jpg";
import upgradeCouples from "~/assets/img/v2/available-styles/upgrade_couples.jpg";
import upgradeHalloween from "~/assets/img/v2/available-styles/upgrade_halloween.jpg";
import upgradeMaternity from "~/assets/img/v2/available-styles/upgrade_maternity.jpg";
import upgradePatriotic from "~/assets/img/v2/available-styles/upgrade_patriotic.jpg";
import upgradeSeasonal from "~/assets/img/v2/available-styles/upgrade_seasonal.jpg";

import basic from "~/assets/img/v2/packages/basic.png";
import team from "~/assets/img/v2/packages/team.png";
import { AvailableStyleType, GridStyle } from "~/pages/result-detail/AvailableStyles";
import { AiFillCheckCircle } from "react-icons/ai";
import { ArrowRightIcon } from "~/components/icons/SvgIcons";
import { createPaymentSession } from "~/redux/dashboards/thunk";
import { appConfig } from "~/config";
import { PAGES } from "~/constants";
import { requestOK } from "redux-thunk-kit";
import { unwrapResult } from "@reduxjs/toolkit";
import { AppError } from "ry-error-wrapper";
import { RootState } from "~/redux/root-reducer";
import { useToast } from "@chakra-ui/react";

const allAccessStyles: AvailableStyleType[] = [
  { img: upgradeChristmas, outfit: "", bg: "Christmas", is_all_access: false },
  { img: upgradeMaternity, outfit: "", bg: "Maternity", is_all_access: false },
  { img: upgradeHalloween, outfit: "", bg: "Halloween", is_all_access: false },
  { img: upgradeCouples, outfit: "", bg: "Couples", is_all_access: false },
  { img: upgradePatriotic, outfit: "", bg: "Patriotic", is_all_access: false },
  { img: upgradeSeasonal, outfit: "", bg: "Seasonal", is_all_access: false },
];

const benefitsIncluded: string[] = [
  "Exclusive access to 35+ styles (new styles added regularly)",
  "Tons of cool styles to choose from (seasonal, couples and much more!)",
  "4K high-resolution photos",
  "Ready in ~2 hours",
];

const benefits: string[] = [
  "Get exclusive access to 35+ styles (new styles added regularly)",
  "4K high-resolution photos",
  "Commercial license",
  "Ready in 2 hours",
];

interface Props extends ModalProps {
  upgradeType: "upsell" | "all_access";
  data?: {
    jobId?: string;
    quantity?: number;
  };
}

function Handler({ data, upgradeType, visible, onClose }: Props) {
  const initialRef = React.useRef(null);
  const dispatch: AppDispatch = useDispatch();
  const jobPendingData = useSelector((state: RootState) => state.dashboard.jobPendingData);
  const [billingPlan, setBillingPlan] = React.useState<"monthly" | "annual">();
  const toast = useToast();
  const orientation = useBreakpointValue<any>({ base: "horizontal", md: "vertical" });
  const vrBillingTitle = useBreakpointValue<any>({ base: "title2", sm: "headline5" });
  const vrBillingSub = useBreakpointValue<any>({ base: "body3", sm: "body2" });
  const vrBillingOrgPrice = useBreakpointValue<any>({ base: "title3", sm: "headline5" });
  const vrBillingPrice = useBreakpointValue<any>({ base: "headline4", sm: "headline1" });
  const vrBillingPerUnit = useBreakpointValue<any>({ base: "body3", sm: "body2" });

  const vrBenefitTitle = useBreakpointValue<any>({ base: "title2", sm: "title1" });
  const vrBenefitList = useBreakpointValue<any>({ base: "body2", sm: "body3" });

  const benefitTips = upgradeType === "all_access" ? benefits : benefitsIncluded;

  const [isLoading, setIsLoading] = React.useState(false);

  const goToPayment = async (type: "upsell" | "all_access") => {
    if (type === "all_access" && !!jobPendingData) {
      toast({
        title: "Pending Jobs",
        description: "You are having pending job, please completed all pending job before purchasing all access plan",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    setIsLoading(true);

    let packageKey: string;
    if (type === "upsell") {
      packageKey = "upsell";
    } else {
      packageKey = billingPlan === "monthly" ? "all-access-subscription-month" : "all-access-subscription-year";
    }
    const postData = {
      cancel_url: `${appConfig.url}${PAGES.PAYMENT_CANCELLED}?session_id={CHECKOUT_SESSION_ID}&package=${packageKey}&job_id=${data?.jobId}&quantity=${data?.quantity}&billing_type=${type}`,
      success_url: `${appConfig.url}${PAGES.PAYMENT_SUCCESSFUL}?session_id={CHECKOUT_SESSION_ID}&package=${packageKey}&job_id=${data?.jobId}&quantity=${data?.quantity}&billing_type=${type}`,
      package_key: packageKey,
      job_id: type === "upsell" ? data?.jobId : undefined,
      quantity: type === "upsell" ? data?.quantity : undefined,
      type,
    };

    const resAction = await dispatch(createPaymentSession({ data: postData }));

    if (requestOK(resAction, createPaymentSession)) {
      const results = unwrapResult(resAction);
      if (results.checkout_url) {
        window.open(results.checkout_url, "_self");
      } else {
        throw new AppError("No checkout URL");
      }
    }
    setIsLoading(false);
  };

  const onCloseModal = () => {
    onClose();
  };

  return !visible ? null : (
    <AppModal
      initialFocusRef={initialRef}
      isOpen={visible}
      onClose={onCloseModal}
      header={null}
      showRightConnerCloseButton
      showFooter={false}
      trapFocus={false}
      closeOnOverlayClick={false}
      scrollBehavior="outside"
      contentMinW={{ base: "80%", md: "50rem" }}
      contentMaxW={{ base: "95%", md: "56.25rem" }}
    >
      <UiFlex flex={1} flexDirection={{ base: "column", md: "row" }} justify="center" py={10} px={{ base: 0 }}>
        <Col minW={{ base: undefined, md: "23.5rem" }}>
          <UiSimpleGrid columns={2} gap={{ base: 2, sm: 3 }}>
            {allAccessStyles.map((item, idx) => (
              <GridStyle key={idx} item={item} maxSize={11.25} hideOutfit />
            ))}
          </UiSimpleGrid>
        </Col>
        <Col>
          <UiDivider my={{ base: 6, md: 0 }} mx={{ base: 0, md: 8 }} orientation={orientation} borderStyle="dashed" />
        </Col>
        <Col>
          <UiText vr="headline3" color="neutral.gray900">
            {upgradeType === "all_access"
              ? "Upgrade to All Access Plan"
              : "WAIT! DO YOU WANT TO UPGRADE TO AN ALL ACCESS PLAN?"}
          </UiText>
          {upgradeType === "all_access" && (
            <UiText vr="body1" color="neutral.gray400" mt={6}>
              Select a billing plan
            </UiText>
          )}

          <Row
            rounded="1rem"
            align="center"
            bg="white"
            px={4}
            py={6}
            mt={4}
            border="1px solid"
            borderColor={billingPlan === "monthly" ? "blue" : "neutral.gray100"}
            minW={{ base: undefined, md: "25.75rem" }}
            pos="relative"
            _hover={{ cursor: "pointer" }}
            onClick={() => setBillingPlan("monthly")}
          >
            <Col pos="absolute" top={{ base: 2, md: 3 }} right={{ base: 2, md: 3 }}>
              {billingPlan === "monthly" && <UiImage src={blueCheckMark} boxSize={6} mx={0} />}
            </Col>
            <UiImage src={basic} boxSize={{ base: 8, md: 10 }} />

            <Col ml={2} flexGrow={1}>
              <UiText vr={vrBillingTitle} color="neutral.gray900">
                Monthly
              </UiText>
              <UiText vr={vrBillingSub} color="neutral.gray400" mt={1}>
                $15 billed monthly
              </UiText>
            </Col>
            <Row align="center" ml={2} mr={2} mt={2}>
              <UiText vr={vrBillingOrgPrice} color="neutral.gray400" textDecoration="line-through">
                $20
              </UiText>
              <UiText vr={vrBillingPrice} color="neutral.gray900" ml={2}>
                $15
              </UiText>
              <UiText vr={vrBillingPerUnit} color="neutral.gray400" mt={{ base: "0.5rem", md: "0.875rem" }} ml={1}>
                / month
              </UiText>
            </Row>
          </Row>
          <Row
            rounded="1rem"
            align="center"
            bg="white"
            px={4}
            py={6}
            mt={4}
            border="1px solid"
            borderColor={billingPlan === "annual" ? "blue" : "neutral.gray100"}
            minW={{ base: undefined, md: "25.75rem" }}
            pos="relative"
            _hover={{ cursor: "pointer" }}
            onClick={() => setBillingPlan("annual")}
          >
            <Col pos="absolute" top={{ base: 2, md: 3 }} right={{ base: 2, md: 3 }}>
              {billingPlan === "annual" && <UiImage src={blueCheckMark} boxSize={6} mx={0} />}
            </Col>
            <UiImage src={team} boxSize={{ base: 8, md: 10 }} />

            <Col ml={2} flexGrow={1}>
              <UiText vr={vrBillingTitle} color="neutral.gray900">
                Annual
              </UiText>
              <UiText vr={vrBillingSub} color="neutral.gray400" mt={1}>
                $120 billed yearly
              </UiText>
            </Col>
            <Row align="center" ml={2} mr={2} mt={2}>
              <UiText vr={vrBillingOrgPrice} color="neutral.gray400" textDecoration="line-through">
                $15
              </UiText>
              <UiText vr={vrBillingPrice} color="neutral.gray900" ml={2}>
                $10
              </UiText>
              <UiText vr={vrBillingPerUnit} color="neutral.gray400" mt={{ base: "0.5rem", md: "0.875rem" }} ml={1}>
                / month
              </UiText>
            </Row>
          </Row>
          <Col mt={6}>
            <UiText vr={vrBenefitTitle}>
              {upgradeType === "all_access" ? "The benefits of Pro Plan" : "What's included"}
            </UiText>

            <UiVStack
              gap={{ base: 2, lg: 3 }}
              mt={{ base: 4, lg: 4 }}
              alignItems="flex-start"
              divider={
                <UiStackDivider
                  borderBottomWidth="0.5px !important"
                  borderColor="neutral.gray100"
                  my="0 !important"
                  ml="2rem !important"
                />
              }
            >
              {benefitTips.map((text, idx) => (
                <Row key={idx} alignItems="center">
                  <UiIcon as={AiFillCheckCircle} mx={0} boxSize={5} color="green" />
                  <UiText vr={vrBenefitList} ml={3} color="neutral.gray700" mt={0}>
                    {text}
                  </UiText>
                </Row>
              ))}
            </UiVStack>
            <Col mt={6}>
              {upgradeType === "all_access" && (
                <UiButton
                  isLoading={isLoading}
                  isDisabled={!billingPlan}
                  h={{ base: "2.5rem", md: "3rem" }}
                  size="sm"
                  variant="outline"
                  borderColor="navy"
                  bg="#353FB5"
                  color="navy"
                  _hover={{ bg: "navy", color: "navy" }}
                  onClick={() => goToPayment("all_access")}
                >
                  <Row align="center" mx={3}>
                    <UiText vr="title1" color="white" mr={2}>
                      Subscribe now!
                    </UiText>
                    <ArrowRightIcon boxSize={5} dColor="white" />
                  </Row>
                </UiButton>
              )}
              {upgradeType === "upsell" && (
                <>
                  <UiButton
                    isLoading={isLoading}
                    isDisabled={!billingPlan}
                    h={{ base: "2.5rem", md: "3rem" }}
                    size="sm"
                    variant="outline"
                    borderColor="navy"
                    bg="#353FB5"
                    color="navy"
                    _hover={{ bg: "navy", color: "navy" }}
                    onClick={() => goToPayment("all_access")}
                  >
                    <Row align="center" mx={3}>
                      <UiText vr="title1" color="white" mr={2}>
                        Go All Access!
                      </UiText>
                      <ArrowRightIcon boxSize={5} dColor="white" />
                    </Row>
                  </UiButton>
                  <UiButton
                    isLoading={isLoading}
                    mt={2}
                    h={{ base: "2.5rem", md: "3rem" }}
                    size="sm"
                    variant="outline"
                    borderColor="darkblue"
                    bg="transparent"
                    color="darkblue"
                    _hover={{
                      bg: "transparent",
                      color: "darkblue",
                    }}
                    onClick={() => goToPayment("upsell")}
                  >
                    <Row align="center" mx={3}>
                      <UiText vr="title1" color="darkblue" mr={2}>
                        No thanks, I&apos;ll pay $2 each per style
                      </UiText>
                    </Row>
                  </UiButton>
                </>
              )}
            </Col>
          </Col>
        </Col>
      </UiFlex>
    </AppModal>
  );
}

function DisclaimerModal({ visible, destroyOnClose = true, ...rest }: Props) {
  if (destroyOnClose) {
    return visible ? <Handler visible {...rest} /> : null;
  }
  return <Handler visible={visible} {...rest} />;
}

export default memo(DisclaimerModal);
